import { EventContextProvider, useAnalytics } from "@codat/analytics";
import {
  Button,
  Card,
  Skeleton,
  Typography,
  useToast,
} from "@codat/orchard-ui";
import {
  InviteValidation,
  useGetRegisterPasswordOptions,
  useRegisterUser,
} from "api/invite";
import { loginSettingsQuery } from "api/login";
import { AxiosError } from "axios";
import React, { FormEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ApiError } from "shared";
import formatUrl from "utils/formatUrl";
import { sanitizeRedirect } from "utils/sanitizeRedirect";
import { useTitle } from "utils/useTitle";

import AuthPage from "components/AuthPage";
import HasValidInvite from "components/HasValidInvite";
import NameInput from "components/NameInput";
import PasswordInputWithValidation from "components/PasswordInputWithValidation";
import LoginByThirdParty from "pages/Login/LoginByThirdParty";

import styles from "./Register.module.css";

export const Register: React.FC = () => {
  const { sendEvent, sendPageViewEvent } = useAnalytics();
  const { inviteId } = useParams<{ inviteId: string }>();
  const queryParams = Object.fromEntries(
    new URLSearchParams(window.location.search)
  );

  const queryParamsWithSafeRedirect = {
    ...queryParams,
    redirectUrl: sanitizeRedirect(queryParams.redirectUrl),
  };

  const redirectUrl = formatUrl(
    "/invite/:inviteId/accept",
    { inviteId },
    queryParamsWithSafeRedirect
  );

  useTitle("Register");

  const successPageRedirect =
    window.location.origin +
    formatUrl(
      "/signin/success",
      {},
      {
        redirectUrl,
      }
    );

  const [, registerUserApi] = useRegisterUser(inviteId, successPageRedirect);
  const [
    {
      loading: passwordOptionsLoading,
      error: passwordOptionsError,
      data: passwordOptions,
    },
  ] = useGetRegisterPasswordOptions(inviteId);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [emailIsValid, setEmailIsValid] = useState(false);
  const [passwordIsValid, setPasswordIsValid] = useState(false);
  const [nameIsValid, setNameIsValid] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [displayErrorsName, setDisplayErrorsName] = useState(false);
  const [displayErrorsPw, setDisplayErrorsPw] = useState(false);
  const [inviteValidation, setInviteValidation] = useState<InviteValidation>();
  const { addToast } = useToast();

  useEffect(() => {
    setIsValid(emailIsValid && passwordIsValid && nameIsValid);
  }, [emailIsValid, passwordIsValid, nameIsValid, setIsValid]);

  useEffect(() => {
    sendPageViewEvent("Register");
  }, [sendPageViewEvent]);

  const [{ loading: loginSettingsLoading, data: loginSettings }] =
    loginSettingsQuery(inviteId);

  const thirdPartyProviders =
    loginSettings?.filter((setting) => setting.isThirdParty === true) || [];
  const hasThirdPartyProviders = (thirdPartyProviders?.length ?? 0) > 0;

  const register = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isValid) {
      setDisplayErrorsName(!nameIsValid);
      setDisplayErrorsPw(!passwordIsValid);
      return;
    }
    registerUserApi({ data: { email, password, name } })
      .then((loginRedirect) => {
        window.location.href = loginRedirect.data.loginRedirect;
      })
      .catch((error: AxiosError<ApiError>) => {
        const errorMessage = error.response?.data?.error ?? "Unknown error";
        addToast({
          message: `Unable to register: ${errorMessage}`,
          variant: "error",
        });
      });
  };

  const handleInviteCallback = (
    inviteValidationCallback: InviteValidation | undefined
  ) => {
    if (inviteValidationCallback) {
      setInviteValidation(inviteValidationCallback);
      setEmail(inviteValidationCallback.inviteEmail);
      setEmailIsValid(true);
      if (inviteValidationCallback.inviteUserName) {
        setName(inviteValidationCallback.inviteUserName);
        setNameIsValid(true);
      }
    }
  };

  if (loginSettingsLoading) {
    return <Skeleton />;
  }

  const formatThirdPartyUrl = () => {
    return (
      window.location.origin +
      formatUrl(
        "/signin/success",
        {},
        {
          redirectUrl,
        }
      )
    );
  };

  const hasLoginByPassword =
    (loginSettings?.filter((setting) => setting.isThirdParty === false)
      ?.length ?? 0) > 0;

  const thirdPartyRedirectUrl = formatThirdPartyUrl();

  return (
    <EventContextProvider name="Register">
      <HasValidInvite
        inviteId={inviteId}
        validateUser={false}
        handleInviteCallback={handleInviteCallback}
      >
        <AuthPage>
          <Card className={styles.registerCard}>
            <Typography variant="heading_xl" as="h1" className={styles.heading}>
              Complete your registration
            </Typography>
            {hasLoginByPassword && (
              <form onSubmit={register}>
                <NameInput
                  dataTestId="name-input"
                  setName={setName}
                  value={name}
                  isNameValid={setNameIsValid}
                  displayErrors={displayErrorsName}
                  disabled={!!inviteValidation?.inviteUserName}
                />
                <PasswordInputWithValidation
                  setPassword={setPassword}
                  isPasswordValid={setPasswordIsValid}
                  displayErrors={displayErrorsPw}
                  loading={passwordOptionsLoading}
                  passwordOptionsError={passwordOptionsError}
                  passwordOptions={passwordOptions}
                />
                <Button
                  className={styles.registerButton}
                  data-testid="register-button"
                  label="Register account"
                  type="submit"
                  disabled={!passwordIsValid}
                  onClick={() => sendEvent("Register Button - Click")}
                />
              </form>
            )}
            {hasThirdPartyProviders && (
              <LoginByThirdParty
                loginSettings={thirdPartyProviders}
                inviteId={inviteId}
                redirectUrl={thirdPartyRedirectUrl}
              />
            )}
          </Card>
        </AuthPage>
      </HasValidInvite>
    </EventContextProvider>
  );
};
